import { useState } from "react";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { Eye, RotateCcw } from "lucide-react";
import { Link } from "react-router";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  Loader,
  PageHeader,
} from "@repo/ui";
import { dateCourteHeure } from "@repo/utils";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { ApercuScorecard } from "./ApercuScorecard";
import { messageErreur } from "./edition";

const TAILLE_PAGE = 20;

function ApercuVersion({
  versionId,
  onFermer,
}: {
  versionId: Id<"scorecardVersions">;
  onFermer: () => void;
}) {
  const version = useQuery(api.scorecards.obtenirVersion, { versionId });
  return (
    <Dialog
      isOpen
      onClose={onFermer}
      title={version ? `Version ${version.numero} — ${version.titre}` : "Version"}
    >
      {version === undefined ? (
        <div className="flex justify-center p-8">
          <Loader />
        </div>
      ) : (
        <div className="space-y-4">
          {version.description && (
            <p className="text-sm text-muted-foreground">{version.description}</p>
          )}
          <ApercuScorecard competences={version.competences} />
        </div>
      )}
    </Dialog>
  );
}

/**
 * Historique d'une scorecard : chaque enregistrement est une version
 * immuable. Restaurer recopie une ancienne version en une NOUVELLE version —
 * rien n'est effacé, et les campagnes gardent celle qu'elles ont figée.
 */
export function HistoriqueVersions({
  scorecardId,
  lienEditeur,
}: {
  scorecardId: Id<"scorecards">;
  lienEditeur: string;
}) {
  const donnees = useQuery(api.scorecards.obtenirScorecard, { scorecardId });
  const versions = usePaginatedQuery(
    api.scorecards.listerVersions,
    { scorecardId },
    { initialNumItems: TAILLE_PAGE },
  );
  const restaurer = useMutation(api.scorecards.restaurerVersion);
  const [apercu, setApercu] = useState<Id<"scorecardVersions"> | null>(null);
  const [aRestaurer, setARestaurer] = useState<number | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  if (donnees === undefined || versions.status === "LoadingFirstPage") {
    return (
      <div className="flex justify-center p-16">
        <Loader />
      </div>
    );
  }

  const { scorecard } = donnees;
  const archivee = scorecard.archivedAt !== undefined;

  const confirmerRestauration = async () => {
    if (aRestaurer === null) return;
    setEnCours(true);
    setErreur(null);
    try {
      await restaurer({
        scorecardId,
        numero: aRestaurer,
        numeroVersionAttendu: scorecard.numeroVersionCourante,
      });
      setARestaurer(null);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  return (
    // `contain: inline-size` : la largeur vient du parent, jamais du contenu.
    // Le `body` du portail client est une grille, qui s'élargirait sinon à la
    // longueur d'un texte de question tronqué (`nowrap`).
    <div className="space-y-6 [contain:inline-size]">
      <PageHeader
        titre={`Historique — ${scorecard.titre}`}
        sousTitre="Chaque enregistrement crée une version. Les nouvelles campagnes reçoivent la version en service ; celles déjà lancées gardent la leur."
        actions={
          <Button variant="outline" asChild>
            <Link to={lienEditeur}>Retour à l'éditeur</Link>
          </Button>
        }
        className="mb-0"
      />
      {erreur && (
        <p role="alert" className="rounded-surface border border-etat-erreur-trait bg-etat-erreur-surface p-3 text-sm">
          {erreur}
        </p>
      )}

      <Card>
        <CardContent className="pt-6">
          <ol className="divide-y divide-border">
            {versions.results.map((v) => {
              const courante = v.numero === scorecard.numeroVersionCourante;
              const enService = v.numero === scorecard.numeroVersionPubliee;
              return (
                <li key={v._id} className="flex flex-wrap items-center gap-3 py-3">
                  <span className="w-24 font-medium tabular-nums">Version {v.numero}</span>
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="truncate">{v.titre}</p>
                    <p className="text-muted-foreground">
                      {dateCourteHeure(v.creeLe)}
                      {v.auteur && ` · ${v.auteur}`}
                      {v.restaureeDepuis !== undefined &&
                        ` · restauration de la version ${v.restaureeDepuis}`}
                      {v.publieeLe !== undefined &&
                        ` · publiée le ${dateCourteHeure(v.publieeLe)}`}
                    </p>
                  </div>
                  {enService && <Badge variant="success">En service</Badge>}
                  {courante && <Badge variant="outline">Courante</Badge>}
                  <Button variant="ghost" size="sm" onClick={() => setApercu(v._id)}>
                    <Eye /> Aperçu
                  </Button>
                  {!courante && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={archivee}
                      onClick={() => setARestaurer(v.numero)}
                    >
                      <RotateCcw /> Restaurer
                    </Button>
                  )}
                </li>
              );
            })}
          </ol>
          {versions.status !== "Exhausted" && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                disabled={versions.status === "LoadingMore"}
                onClick={() => versions.loadMore(TAILLE_PAGE)}
              >
                Versions plus anciennes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {apercu && <ApercuVersion versionId={apercu} onFermer={() => setApercu(null)} />}

      <Dialog
        isOpen={aRestaurer !== null}
        onClose={() => setARestaurer(null)}
        title={`Restaurer la version ${aRestaurer ?? ""} ?`}
        className="max-w-lg"
      >
        <p className="mb-6 text-sm text-muted-foreground">
          Son contenu deviendra la version {scorecard.numeroVersionCourante + 1},
          une version de travail : publiez-la ensuite depuis l'éditeur pour
          qu'elle serve aux nouvelles campagnes. Rien n'est effacé de
          l'historique.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setARestaurer(null)}>
            Annuler
          </Button>
          <Button disabled={enCours} onClick={() => void confirmerRestauration()}>
            Restaurer
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
